import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Save, Refresh } from "@material-ui/icons";
import MarkdownEditorWithPreview from "components/MarkdownEditor/MarkdownEditorWithPreview";
import Button from "components/CustomButtons/Button.js";
import NotificationContext from "views/Components/Context.js";
import {
  listInputs,
  uploadInstructions,
  updateInstructions,
  fetchMarkdownFromS3,
} from "views/APIs/NewProjectArtifacts";

import {
  DEFAULT_INSTRUCTIONS,
  PIPELINE_STAGE,
} from "./artifactProjectConstants";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    marginBottom: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  selectContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  formControl: {
    minWidth: 250,
  },
  editorContainer: {
    marginTop: theme.spacing(2),
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  wrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: theme.palette.success.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const INSTRUCTION_STAGE_MAPPING = {
  0: 'Case Study Creation',
  1: 'Outline Creation',
  2: 'Slide Deck Creation',
  3: 'Lecture Notes Creation',
  4: 'Slide Deck with Notes Creation',
};

function getDateFromObjectId(objectId) {
  // The first 8 characters of the hex string represent the timestamp (4 bytes)
  const timestampHex = objectId.substring(0, 8);

  // Convert hex to an integer (seconds)
  const timestampSeconds = parseInt(timestampHex, 16);

  // Convert seconds to milliseconds for the JavaScript Date object
  return new Date(timestampSeconds * 1000);
}

const InstructionsComponent = React.forwardRef(function InstructionsComponent({
  projectId,
  stage,
  onInstructionsChange,
  onTemplateChange,
  autoCreateIfMissing = false,
  disabled = false,
}, ref) {
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);

  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [instructionsList, setInstructionsList] = useState([]);
  const [selectedInstructionId, setSelectedInstructionId] = useState("");
  const [markdownContent, setMarkdownContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("GMU");

  // Track stage changes so we only auto-select on initial load or true stage transitions.
  const prevStageRef = useRef(stage);

  // Avoid repeatedly auto-creating instructions for the same project/stage.
  const autoCreateAttemptedRef = useRef(new Set());

  // Track whether we've completed at least one fetch for this project.
  const hasFetchedOnceRef = useRef(false);

  // Fetch instructions list
  const fetchInstructions = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const data = await listInputs(projectId);
      const instructions = data.instructions || [];
      setInstructionsList(instructions);
    } catch (error) {
      console.error("Error fetching instructions:", error);
      showNotification("Error loading instructions", "danger");
    } finally {
      setLoading(false);
      hasFetchedOnceRef.current = true;
    }
  };

  // Load content from S3 URL
  const loadInstructionContent = async (instruction) => {
    if (!instruction?.s3?.public_url) return;

    try {
      const data = await fetchMarkdownFromS3(
        projectId,
        instruction.s3.public_url,
      );
      const content = data?.markdown ?? "";
      setMarkdownContent(content);
      setOriginalContent(content);
      setHasUnsavedChanges(false);

      if (onInstructionsChange) {
        onInstructionsChange(instruction.input_file_id, content);
      }
    } catch (error) {
      console.error("Error loading instruction content:", error);
      showNotification("Error loading instruction content", "danger");
    }
  };

  // Handle instruction selection change
  const handleInstructionSelect = async (event) => {
    const inputFileId = event.target.value;
    setSelectedInstructionId(inputFileId);

    if (onInstructionsChange) {
      onInstructionsChange(inputFileId, "");
    }

    const selectedInstruction = instructionsList.find(
      (instr) => instr.input_file_id === inputFileId,
    );

    if (selectedInstruction) {
      await loadInstructionContent(selectedInstruction);
    }
  };

  // Handle markdown content change
  const handleContentChange = (newContent) => {
    setMarkdownContent(newContent);
    setHasUnsavedChanges(newContent !== originalContent);
  };

  // Handle template change
  const handleTemplateChange = (event) => {
    const template = event.target.value;
    setSelectedTemplate(template);
    if (onTemplateChange) {
      onTemplateChange(template);
    }
  };

  // Save instructions
  const handleSave = async () => {
    if (!selectedInstructionId || !markdownContent) {
      showNotification("No instruction selected to save", "warning");
      return false;
    }

    setSaving(true);
    try {
      await updateInstructions(
        projectId,
        selectedInstructionId,
        markdownContent,
      );
      setOriginalContent(markdownContent);
      setHasUnsavedChanges(false);
      showNotification("Instructions saved successfully", "success");

      if (onInstructionsChange) {
        onInstructionsChange(selectedInstructionId, markdownContent);
      }
      return true;
    } catch (error) {
      console.error("Error saving instructions:", error);
      showNotification("Error saving instructions", "danger");
      return false;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      hasUnsavedChanges: () => hasUnsavedChanges,
      saveIfNeeded: async () => {
        if (!hasUnsavedChanges) return true;
        if (saving) return false;
        return handleSave();
      },
    }),
    [hasUnsavedChanges, saving, selectedInstructionId, markdownContent, projectId]
  );

  // Create new instructions
  const handleCreateNew = async () => {
    if (!projectId) return;

    setSaving(true);
    try {
      // Create a new instructions file with default content from constants
      const defaultContent =
        DEFAULT_INSTRUCTIONS[stage] ||
        `# Instructions for Stage ${stage}\n\nAdd your instructions here...`;

      const result = await uploadInstructions(projectId, defaultContent, stage);

      // Refresh the list and select the new instruction
      await fetchInstructions();
      setSelectedInstructionId(result.input_file_id);
      setMarkdownContent(defaultContent);
      setOriginalContent(defaultContent);
      setHasUnsavedChanges(false);

      if (onInstructionsChange) {
        onInstructionsChange(result.input_file_id, defaultContent);
      }

      showNotification("New instructions created", "success");
    } catch (error) {
      console.error("Error creating instructions:", error);
      showNotification("Error creating instructions", "danger");
    } finally {
      setSaving(false);
    }
  };

  // Refresh instructions list
  const handleRefresh = () => {
    fetchInstructions();
  };

  // Initial fetch
  useEffect(() => {
    fetchInstructions();
  }, [projectId]);

  // Auto-create missing stage instructions (if enabled)
  useEffect(() => {
    if (!autoCreateIfMissing) return;
    if (!projectId) return;
    if (!hasFetchedOnceRef.current) return;
    if (loading || saving) return;

    const hasStageInstruction = instructionsList.some(
      (instr) => instr.pipeline_stage === stage
    );

    if (hasStageInstruction) return;

    const key = `${projectId}:${stage}`;
    if (autoCreateAttemptedRef.current.has(key)) return;
    autoCreateAttemptedRef.current.add(key);

    (async () => {
      setSaving(true);
      try {
        const defaultContent =
          DEFAULT_INSTRUCTIONS[stage] ||
          `# Instructions for Stage ${stage}\n\nAdd your instructions here...`;

        await uploadInstructions(projectId, defaultContent, stage);
        await fetchInstructions();
      } catch (error) {
        console.error("Error auto-creating instructions:", error);
        showNotification("Error auto-creating instructions", "danger");
        autoCreateAttemptedRef.current.delete(key);
      } finally {
        setSaving(false);
      }
    })();
  }, [autoCreateIfMissing, projectId, stage, instructionsList, loading, saving]);

  // Auto-select the instruction matching the current stage whenever stage or list changes
  useEffect(() => {
    if (instructionsList.length === 0) return;

    const stageChanged = prevStageRef.current !== stage;
    prevStageRef.current = stage;

    const selectedInstruction = selectedInstructionId
      ? instructionsList.find((instr) => instr.input_file_id === selectedInstructionId)
      : null;
    const selectedStillExists = !!selectedInstruction;
    const selectedIsForCurrentStage = selectedInstruction?.pipeline_stage === stage;

    // Don't override the user's selection only if: stage hasn't changed AND the
    // currently selected instruction is already for the correct stage.
    if (!stageChanged && selectedInstructionId && selectedStillExists && selectedIsForCurrentStage) return;

    const stageInstruction = instructionsList.find(
      (instr) => instr.pipeline_stage === stage
    );
    const target = stageInstruction || instructionsList[0];

    if (target && target.input_file_id !== selectedInstructionId) {
      setSelectedInstructionId(target.input_file_id);

      if (onInstructionsChange) {
        onInstructionsChange(target.input_file_id, "");
      }
      loadInstructionContent(target);
    }
  }, [stage, instructionsList, selectedInstructionId]);

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={24} />
        <Typography variant="body2" style={{ marginLeft: 8 }}>
          Loading instructions...
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          Instructions for Next Step
        </Typography>
        <Box className={classes.actionButtons}>
          <Tooltip title="Refresh list">
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            color="quCreateColor"
            size="sm"
            onClick={handleCreateNew}
            disabled={saving || disabled}
          >
            New Instructions for this stage
          </Button>
        </Box>
      </Box>

      <Box className={classes.selectContainer}>
        <FormControl
          variant="outlined"
          className={classes.formControl}
          size="small"
        >
          <InputLabel id="instructions-select-label">
            Select Instructions
          </InputLabel>
          <Select
            labelId="instructions-select-label"
            value={selectedInstructionId}
            onChange={handleInstructionSelect}
            label="Select Instructions"
            disabled={disabled || instructionsList.length === 0}
          >
            {instructionsList.length === 0 ? (
              <MenuItem value="" disabled>
                No instructions available
              </MenuItem>
            ) : (
              instructionsList.map((instr) => {
                const date = getDateFromObjectId(instr.input_file_id);
                const stageInfo = 
                instr.pipeline_stage !== null ?
                `${INSTRUCTION_STAGE_MAPPING[instr.pipeline_stage] || instr.pipeline_stage}` :
                "instructions.md";
                return (
                  <MenuItem
                    key={instr.input_file_id}
                    value={instr.input_file_id}
                  >
                    {stageInfo}
                    {` - ${date.toLocaleString()}`}
                  </MenuItem>
                );
              })
            )}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel id="template-select-label">Template</InputLabel>
          <Select
            labelId="template-select-label"
            value={selectedTemplate}
            onChange={handleTemplateChange}
            label="Template"
            disabled={disabled}
          >
            <MenuItem value="GMU">GMU</MenuItem>
            <MenuItem value="NEU">NEU</MenuItem>
          </Select>
        </FormControl>

        {(hasUnsavedChanges || saving) && (
          <div className={classes.wrapper}>
            <Button
              color="success"
              size="sm"
              onClick={handleSave}
              disabled={saving || disabled}
              startIcon={!saving ? <Save /> : null}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
            {saving && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </div>
        )}
      </Box>

      <Box className={classes.editorContainer}>
        {instructionsList.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No instructions available. Click "New Instructions" to create one.
          </Typography>
        ) : (
          <MarkdownEditorWithPreview
            value={markdownContent}
            onChange={handleContentChange}
            disabled={disabled || !selectedInstructionId}
            height="150px"
            placeholder="Before editing these instructions, please click 'New Instructions' to create a new instruction file. Then select it from the dropdown to edit."
          />
        )}
      </Box>
    </Box>
  );
});

export default InstructionsComponent;
